import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';

export interface UaTransformArtikel {
    ba_id: number;
    artikel_id: number;
    artikel: string;
    anzahl: number;
    notiz: string;
    selected: boolean;
}

export interface UaCustomField {
    key: string;
    label: string;
    type?: 'text' | 'checkbox';
    value?: string;
}

export interface UaTransformFormular {
    ld_id: number;
    lieferant_id: number;
    dokument_typ?: number;
    tierart_id?: number;
    tierart?: string;
    bezeichnung: string;
    pdf_id: number;
    dateiname: string;
    hat_mapping?: number;
    custom_fields?: UaCustomField[];
    overlays?: UaFillOverlay[];
}

export interface UaFillOverlay {
    page: number;
    x: number;
    y: number;
    size: number;
    placeholder: string;
    width?: number;
    lines?: number;
}

export interface UaTransformLabor {
    lieferant_id: number;
    lieferant: string;
    /** Eine Zeile je Artikel, damit jedes Formular für sich steht. */
    rowKey: number;
    formulare: UaTransformFormular[];
    lieferant_dokument_id: number;
    artikel: UaTransformArtikel[];
    selected: boolean;
    /** key → Wert für Freitext-Platzhalter */
    formularWerte: { [key: string]: string };
}

@Component({
    selector: 'kiene-untersuchungsauftrag-transform-dialog',
    templateUrl: './untersuchungsauftrag-transform-dialog.component.html',
    styleUrls: ['./untersuchungsauftrag-transform-dialog.component.scss'],
})
export class UntersuchungsauftragTransformDialogComponent implements OnInit, OnDestroy {
    @ViewChildren('pdfHost') pdfHosts: QueryList<ElementRef<HTMLElement>>;

    loading = true;
    labore: UaTransformLabor[] = [];
    befundId: number;
    fillPage: { [lid: number]: number } = {};
    fillPageCount: { [lid: number]: number } = {};
    fillPages: { [lid: number]: { page: number; width: number; height: number }[] } = {};
    fillPdfSrc: { [lid: number]: Uint8Array } = {};
    fillPdfLoading: { [lid: number]: boolean } = {};
    fillPdfError: { [lid: number]: string } = {};
    fillLayoutTick = 0;
    private fillScale: { [lid: number]: number } = {};
    private readonly onResize = () => this.labore.forEach(l => this.placeMarks(l));

    constructor(
        public dialogRef: MatDialogRef<UntersuchungsauftragTransformDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { befund_id: number },
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private messageService: MessageService,
        private pdfViewer: PdfViewerDesktopService,
        private http: HttpClient,
        private cd: ChangeDetectorRef,
    ) {
        this.befundId = data.befund_id;
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this.onResize);
    }

    ngOnInit(): void {
        window.addEventListener('resize', this.onResize);
        const params = new HttpParams().set('befund_id', String(this.befundId));
        this.api.get(this.apiBaseUrl + 'untersuchungsauftrag/transform_vorschau.php', params).subscribe({
            next: (response: any) => {
                this.labore = this.zeilenJeArtikel(response.labore || []);
                this.labore.forEach(l => {
                    this.ensureFormularWerte(l);
                    if (l.lieferant_dokument_id) {
                        this.loadFillPdf(l);
                    }
                });
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('Vorschau konnte nicht geladen werden!', err);
                this.dialogRef.close();
            },
        });
    }

    /** Ein Formular gehört zu einem Artikel, nicht zum ganzen Labor. */
    private zeilenJeArtikel(labore: any[]): UaTransformLabor[] {
        const zeilen: UaTransformLabor[] = [];
        for (const labor of labore) {
            const artikel = labor.artikel?.length ? labor.artikel : [];
            for (const a of artikel) {
                zeilen.push({
                    ...labor,
                    rowKey: a.ba_id,
                    artikel: [a],
                    formularWerte: {},
                });
            }
        }
        return zeilen;
    }

    private key(labor: UaTransformLabor): number {
        return labor.rowKey;
    }

    formularOptionLabel(f: UaTransformFormular): string {
        return f.bezeichnung || f.dateiname || '#' + f.ld_id;
    }

    selectedFormular(labor: UaTransformLabor): UaTransformFormular | null {
        return labor.formulare?.find(f => f.ld_id === labor.lieferant_dokument_id) || null;
    }

    customFieldsFor(labor: UaTransformLabor): UaCustomField[] {
        return this.selectedFormular(labor)?.custom_fields || [];
    }

    onFormularChange(labor: UaTransformLabor) {
        this.ensureFormularWerte(labor);
        this.loadFillPdf(labor);
    }

    overlaysFor(labor: UaTransformLabor): UaFillOverlay[] {
        return this.selectedFormular(labor)?.overlays || [];
    }

    overlaysOnPage(labor: UaTransformLabor): UaFillOverlay[] {
        const page = this.fillPage[this.key(labor)] || 1;
        return this.overlaysFor(labor).filter(o => (o.page || 1) === page);
    }

    isTextBlock(_o: UaFillOverlay): boolean {
        return false;
    }

    /** Eine Zeile, Breite wie der aufgezogene Block. */
    fillLineStyle(labor: UaTransformLabor, o: UaFillOverlay): { [k: string]: string } {
        const base = this.fillMarkerStyle(labor, o);
        if ((o.width || 0) <= 12) {
            return { ...base, width: '140px' };
        }
        const info = (this.fillPages[this.key(labor)] || []).find(p => p.page === (o.page || 1));
        const w = info?.width || 595;
        return {
            ...base,
            width: ((o.width || 0) / w) * 100 + '%',
            maxWidth: 'none',
            boxSizing: 'border-box',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        };
    }

    fillBlockStyle(labor: UaTransformLabor, o: UaFillOverlay): { [k: string]: string } {
        const base = this.fillMarkerStyle(labor, o);
        const info = (this.fillPages[this.key(labor)] || []).find(p => p.page === (o.page || 1));
        const w = info?.width || 595;
        const h = info?.height || 842;
        const size = o.size || 9;
        const lines = o.lines || 1;
        return {
            ...base,
            width: ((o.width || 0) / w) * 100 + '%',
            height: ((lines * size * 1.25) / h) * 100 + '%',
        };
    }

    overlayKind(labor: UaTransformLabor, o: UaFillOverlay): 'text' | 'checkbox' | 'context' {
        const field = this.customFieldsFor(labor).find(f => f.key === o.placeholder);
        if (!field) {
            return 'context';
        }
        return field.type === 'checkbox' ? 'checkbox' : 'text';
    }

    overlayLabel(labor: UaTransformLabor, o: UaFillOverlay): string {
        const field = this.customFieldsFor(labor).find(f => f.key === o.placeholder);
        if (field) {
            return field.label;
        }
        const tail = (o.placeholder || '').split('.').pop() || o.placeholder;
        return tail.replace(/_/g, ' ');
    }

    toggleCheckbox(labor: UaTransformLabor, key: string) {
        labor.formularWerte[key] = labor.formularWerte[key] === '1' ? '0' : '1';
    }

    prevFillPage(labor: UaTransformLabor) {
        const page = this.fillPage[this.key(labor)] || 1;
        if (page > 1) {
            this.fillPage[this.key(labor)] = page - 1;
        }
    }

    nextFillPage(labor: UaTransformLabor) {
        const page = this.fillPage[this.key(labor)] || 1;
        const count = this.fillPageCount[this.key(labor)] || 1;
        if (page < count) {
            this.fillPage[this.key(labor)] = page + 1;
        }
    }

    onFillPdfLoad(
        labor: UaTransformLabor,
        pdf: {
            numPages?: number;
            getPage?: (n: number) => Promise<{ getViewport: (o: { scale: number }) => { width: number; height: number } }>;
        },
    ) {
        if (pdf?.numPages && pdf.numPages > 0) {
            this.fillPageCount[this.key(labor)] = Math.max(this.fillPageCount[this.key(labor)] || 1, pdf.numPages);
        }
        if (pdf?.getPage && pdf.numPages) {
            const n = pdf.numPages;
            Promise.all(
                Array.from({ length: n }, (_, i) =>
                    pdf.getPage!(i + 1).then(page => {
                        const vp = page.getViewport({ scale: 1 });
                        return { page: i + 1, width: vp.width, height: vp.height };
                    }),
                ),
            ).then(pages => {
                this.fillPages[this.key(labor)] = pages;
                this.placeMarks(labor);
            });
        }
        setTimeout(() => this.placeMarks(labor), 0);
    }

    /** Legt die Eingaben auf die Canvas. Scroll verschiebt nur die Hülle, ohne Angular neu zu rechnen. */
    placeMarks(labor: UaTransformLabor) {
        const host = this.hostFor(labor);
        if (!host) {
            return;
        }
        const marks = host.querySelector('.ua-pdf-marks') as HTMLElement;
        const scroller = host.querySelector('.ng2-pdf-viewer-container') as HTMLElement;
        if (!marks || !scroller) {
            return;
        }
        const sync = () => {
            const canvas = host.querySelector('.page canvas') as HTMLElement;
            if (!canvas) {
                return;
            }
            const hostRect = host.getBoundingClientRect();
            const canvasRect = canvas.getBoundingClientRect();
            if (canvasRect.width <= 0 || canvasRect.height <= 0) {
                return;
            }
            marks.style.transform =
                'translate(' + (canvasRect.left - hostRect.left) + 'px,' + (canvasRect.top - hostRect.top) + 'px)';
            marks.style.width = canvasRect.width + 'px';
            marks.style.height = canvasRect.height + 'px';
        };
        sync();
        const scrollerAny = scroller as HTMLElement & { __uaSync?: boolean; __uaRaf?: number };
        if (!scrollerAny.__uaSync) {
            scrollerAny.__uaSync = true;
            scroller.addEventListener(
                'scroll',
                () => {
                    if (scrollerAny.__uaRaf) {
                        return;
                    }
                    scrollerAny.__uaRaf = requestAnimationFrame(() => {
                        scrollerAny.__uaRaf = 0;
                        sync();
                    });
                },
                { passive: true },
            );
        }
        const canvas = host.querySelector('.page canvas') as HTMLElement;
        if (!canvas || canvas.clientWidth <= 0) {
            return;
        }
        const pageNo = this.fillPage[this.key(labor)] || 1;
        const info = (this.fillPages[this.key(labor)] || []).find(p => p.page === pageNo);
        const next = canvas.clientWidth / (info?.width || 595);
        if (Math.abs((this.fillScale[this.key(labor)] || 0) - next) > 0.02) {
            this.fillScale[this.key(labor)] = next;
            this.fillLayoutTick++;
            this.cd.detectChanges();
        }
    }

    fillMarkerStyle(labor: UaTransformLabor, o: UaFillOverlay): { [k: string]: string } {
        void this.fillLayoutTick;
        const info = (this.fillPages[this.key(labor)] || []).find(p => p.page === (o.page || 1));
        const w = info?.width || 595;
        const h = info?.height || 842;
        const scale = this.fillScale[this.key(labor)] || 1;
        return {
            left: (o.x / w) * 100 + '%',
            top: ((h - o.y) / h) * 100 + '%',
            fontSize: Math.max(11, Math.round((o.size || 9) * scale)) + 'px',
        };
    }

    private hostFor(labor: UaTransformLabor): HTMLElement | null {
        const found = this.pdfHosts?.find(h => h.nativeElement.dataset.lid === String(this.key(labor)));
        return found?.nativeElement || null;
    }

    private loadFillPdf(labor: UaTransformLabor) {
        const lid = this.key(labor);
        const ldId = labor.lieferant_dokument_id;
        delete this.fillPdfSrc[lid];
        this.fillPdfError[lid] = '';
        this.fillPage[lid] = 1;
        if (!ldId || !this.overlaysFor(labor).length) {
            this.fillPdfLoading[lid] = false;
            return;
        }
        this.fillPdfLoading[lid] = true;
        this.http
            .get(this.apiBaseUrl + 'lieferant_dokument/read_pdf.php', {
                params: new HttpParams().set('ld_id', String(ldId)),
                responseType: 'blob',
            })
            .subscribe({
                next: blob => {
                    blob.arrayBuffer().then(buf => {
                        const bytes = new Uint8Array(buf);
                        if (new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-') {
                            this.fillPdfLoading[lid] = false;
                            this.fillPdfError[lid] = 'Formular-PDF konnte nicht geladen werden.';
                            return;
                        }
                        this.fillPdfSrc[lid] = bytes;
                        this.fillPdfLoading[lid] = false;
                        this.cd.detectChanges();
                    });
                },
                error: () => {
                    this.fillPdfLoading[lid] = false;
                    this.fillPdfError[lid] = 'Formular-PDF konnte nicht geladen werden.';
                },
            });
    }

    private ensureFormularWerte(labor: UaTransformLabor) {
        if (!labor.formularWerte) {
            labor.formularWerte = {};
        }
        const fields = this.customFieldsFor(labor);
        const next: { [key: string]: string } = {};
        for (const f of fields) {
            const prev = labor.formularWerte[f.key];
            if (f.type === 'checkbox') {
                next[f.key] = prev === '1' || prev === '0' ? prev : '';
            } else {
                next[f.key] = prev ?? '';
            }
        }
        labor.formularWerte = next;
    }

    private freitextMissing(f: UaCustomField, wert: string | undefined): boolean {
        if (f.type === 'checkbox') {
            return wert !== '0' && wert !== '1';
        }
        return !(wert || '').trim();
    }

    canSubmit(): boolean {
        return this.labore.some(l => l.selected && l.lieferant_dokument_id && l.artikel.some(a => a.selected));
    }

    missingFormulare(): string[] {
        return this.labore
            .filter(l => l.selected && l.artikel.some(a => a.selected) && !l.lieferant_dokument_id)
            .map(l => l.lieferant + (l.artikel[0]?.artikel ? ' / ' + l.artikel[0].artikel : ''));
    }

    missingFreitexte(): string[] {
        const out: string[] = [];
        for (const l of this.labore) {
            if (!l.selected || !l.artikel.some(a => a.selected)) {
                continue;
            }
            for (const f of this.customFieldsFor(l)) {
                if (this.freitextMissing(f, l.formularWerte?.[f.key])) {
                    out.push((l.artikel[0]?.artikel || l.lieferant) + ': ' + f.label);
                }
            }
        }
        return out;
    }

    previewFormular(ldId: number, name: string) {
        this.pdfViewer.openPdfViewer(
            new PdfViewerDialogInput(
                'lieferant_dokument/read_pdf.php',
                new HttpParams().set('ld_id', String(ldId)),
                name || 'Untersuchungsformular',
            ),
        );
    }

    cancel() {
        this.dialogRef.close();
    }

    submit() {
        const missing = this.missingFormulare();
        if (missing.length) {
            this.messageService.infoMessage(
                'Formular fehlt für: ' + missing.join(', ') + '. Bitte am Lieferanten hinterlegen.',
            );
            return;
        }
        const auswahl = this.labore
            .filter(l => l.selected && l.artikel.some(a => a.selected))
            .map(l => ({
                lieferant_id: l.lieferant_id,
                lieferant_dokument_id: l.lieferant_dokument_id,
                ba_ids: l.artikel.filter(a => a.selected).map(a => a.ba_id),
                formular_werte: l.formularWerte || {},
            }));
        this.dialogRef.close({ befund_id: this.befundId, auswahl });
    }
}
